import React, { useEffect, useState } from 'react';
import { createUser, fetchChoices, updateUser } from '../api';
import { displayName } from './format';

// Add or edit a staff user. Changing someone's password or permissions here doesn't sign
// them out anywhere.

const PERMISSIONS = [
  { name: 'is_admin', label: 'Admin', help: 'Admin Settings, users, choices and the Commercial Portal page' },
  { name: 'is_sales_base', label: 'Sales Base', help: 'Sees prices and uses QuickBooks' },
  { name: 'is_sales_advanced', label: 'Sales Advanced', help: 'The Inbox and sales features. Includes Sales Base.' },
];

const FIELD_LABELS = {
  username: 'Username', email: 'Email', password: 'Password', first_name: 'First name', last_name: 'Last name',
  initials: 'Initials', default_contact: 'Default contact',
};

const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  if (data && typeof data === 'object') {
    const parts = Object.entries(data).map(([field, value]) => {
      const text = [].concat(value).join(' ');
      return FIELD_LABELS[field] ? `${FIELD_LABELS[field]}: ${text}` : text;
    });
    if (parts.length) return parts.join(' ');
  }
  return fallback;
};

function Switch({ name, checked, disabled, onChange, labelledBy }) {
  return (
    <label className="profile-switch">
      <input
        type="checkbox"
        role="switch"
        name={name}
        aria-labelledby={labelledBy}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
      <span className="profile-switch-slider" />
    </label>
  );
}

function UserForm({ user, isYou, onSaved, onDelete, onCancel }) {
  const isNew = !user;
  const [form, setForm] = useState({
    username: user?.username || '',
    email: user?.email || '',
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    password: '',
    initials: user?.initials || '',
    default_contact: user?.default_contact || '',
    filter_jobs_by_default_contact: !!user?.filter_jobs_by_default_contact,
    is_admin: !!user?.is_admin,
    is_sales_advanced: !!user?.is_sales_advanced,
    is_sales_base: !!user?.is_sales_base,
  });
  const [contactChoices, setContactChoices] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchChoices()
      .then((choices) => setContactChoices([...(choices.contact || [])].sort((a, b) => a.order - b.order)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape' && !saving) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, saving]);

  const set = (name, value) => setForm((prev) => ({
    ...prev,
    [name]: value,
    // Sales Advanced includes Sales Base
    ...(name === 'is_sales_advanced' && value ? { is_sales_base: true } : {}),
  }));
  const onField = (event) => {
    const { name, value, type, checked } = event.target;
    set(name, type === 'checkbox' ? checked : value);
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setSaving(true);
    const data = { ...form, filter_jobs_by_default_contact: !!form.default_contact && form.filter_jobs_by_default_contact };
    if (!data.password) delete data.password;
    try {
      if (isNew) await createUser(data);
      else await updateUser(user.id, data);
      onSaved();
    } catch (err) {
      setError(errorMessage(err, isNew ? 'Could not create the user.' : 'Could not save the changes.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="um-overlay" role="dialog" aria-modal="true" aria-labelledby="um-form-title">
      <form className="um-dialog" onSubmit={submit}>
        <div className="um-dialog-header">
          <h3 id="um-form-title">{isNew ? 'Add user' : `Edit ${displayName(user)}`}</h3>
          <button type="button" className="um-close" onClick={onCancel} aria-label="Close">×</button>
        </div>

        <div className="um-dialog-body">
          <fieldset className="um-fieldset">
            <legend>Sign-in</legend>
            <div className="um-grid">
              <label className="um-field">
                <span>Username</span>
                <input name="username" value={form.username} onChange={onField} required autoComplete="off" autoFocus={isNew} />
              </label>
              <label className="um-field">
                <span>{isNew ? 'Password' : 'New password'}</span>
                <input
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={onField}
                  required={isNew}
                  autoComplete="new-password"
                  placeholder={isNew ? '' : 'Leave blank to keep it'}
                />
              </label>
            </div>
          </fieldset>

          <fieldset className="um-fieldset">
            <legend>Details</legend>
            <div className="um-grid">
              <label className="um-field">
                <span>First name</span>
                <input name="first_name" value={form.first_name} onChange={onField} />
              </label>
              <label className="um-field">
                <span>Last name</span>
                <input name="last_name" value={form.last_name} onChange={onField} />
              </label>
              <label className="um-field">
                <span>Email{isNew ? '' : <em className="um-optional"> optional</em>}</span>
                <input name="email" type="email" value={form.email} onChange={onField} required={isNew} />
              </label>
              <label className="um-field um-field-small">
                <span>Initials</span>
                <input
                  name="initials"
                  value={form.initials}
                  onChange={(event) => set('initials', event.target.value.toUpperCase())}
                  maxLength={3}
                  placeholder="ABC"
                />
              </label>
            </div>
          </fieldset>

          <fieldset className="um-fieldset">
            <legend>Job defaults</legend>
            <div className="um-toggle-row">
              <div className="um-toggle-text">
                <span className="um-toggle-label">Default contact</span>
                <span className="um-muted">Picked automatically on their new jobs</span>
              </div>
              <select name="default_contact" value={form.default_contact} onChange={onField} className="um-select">
                <option value="">None</option>
                {contactChoices.map((choice) => (
                  <option key={choice.value} value={choice.value}>{choice.display_name}</option>
                ))}
                {form.default_contact && !contactChoices.some((c) => c.value === form.default_contact) && (
                  <option value={form.default_contact}>{form.default_contact}</option>
                )}
              </select>
            </div>
            <div className={`um-toggle-row ${form.default_contact ? '' : 'um-disabled'}`}>
              <div className="um-toggle-text">
                <span className="um-toggle-label" id="um-filter-label">Open the job list on their contact</span>
                <span className="um-muted">
                  {form.default_contact ? 'They can still pick other contacts in the job list.' : 'Choose a default contact first.'}
                </span>
              </div>
              <Switch
                name="filter_jobs_by_default_contact"
                labelledBy="um-filter-label"
                checked={!!form.default_contact && form.filter_jobs_by_default_contact}
                disabled={!form.default_contact}
                onChange={onField}
              />
            </div>
          </fieldset>

          <fieldset className="um-fieldset">
            <legend>Permissions</legend>
            {PERMISSIONS.map((p) => {
              const impliedBase = p.name === 'is_sales_base' && form.is_sales_advanced;
              return (
                <div key={p.name} className={`um-toggle-row ${impliedBase ? 'um-disabled' : ''}`}>
                  <div className="um-toggle-text">
                    <span className="um-toggle-label" id={`um-${p.name}`}>{p.label}</span>
                    <span className="um-muted">{impliedBase ? 'Included with Sales Advanced' : p.help}</span>
                  </div>
                  <Switch
                    name={p.name}
                    labelledBy={`um-${p.name}`}
                    checked={impliedBase || form[p.name]}
                    disabled={impliedBase}
                    onChange={onField}
                  />
                </div>
              );
            })}
          </fieldset>

          {error && <div className="um-error">{error}</div>}
        </div>

        <div className="um-dialog-footer">
          {onDelete && (
            <button
              type="button"
              className="um-btn um-btn-danger-link"
              onClick={onDelete}
              disabled={isYou || saving}
              title={isYou ? "You can't delete your own account" : 'Delete this user'}
            >
              Delete user
            </button>
          )}
          <span className="um-spacer" />
          <button type="button" className="um-btn" onClick={onCancel} disabled={saving}>Cancel</button>
          <button type="submit" className="um-btn um-btn-primary" disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Add user' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default UserForm;
